import { gsap } from 'gsap';
import { connect } from 'react-redux';
import { SplitText } from 'gsap/SplitText';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link, withRouter } from 'react-router-dom';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import './index.css';
import logo from '../../static/img/logo.svg';
import { forgotPassword } from '../../store/Auth';
import { getBranding } from '../../store/Settings';
gsap.registerPlugin(SplitText, ScrollTrigger);
const ForgotPassword = ({ history }) => {
  const dispatch = useDispatch();
  const { currentBranding } = useSelector(state => state['Settings']);
  const headingRefs = useRef([]);
  const paragraphRefs = useRef([]);
  const splitParagraphs = useRef([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false)

  const platformName = (currentBranding?.isCustom && currentBranding?.agencyName) ? currentBranding.agencyName : 'Sales AI';

  useEffect(() => { document.title = `Forgot Password | ${platformName}` }, [platformName]);

  const handleLogin = () => {
    setLoading(true)
    dispatch(forgotPassword({
      formData: { email }, successCallback: () => {
        setLoading(false)
        history.push('/reset-password')
      }, failCallback: () => setLoading(false)
    }))
  }

  useEffect(() => {
    // Reset any previous SplitText instances
    splitParagraphs.current.forEach((split) => split?.revert?.());
    splitParagraphs.current = [];

    // Animate each heading
    headingRefs.current.forEach((el, i) => {
      gsap.fromTo(
        el,
        { opacity: 0, filter: "blur(10px)", y: -20 },
        {
          opacity: 1,
          filter: "blur(0px)",
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          delay: i * 0.3,
        }
      );
    });

    // Animate each paragraph with SplitText
    paragraphRefs.current.forEach((el, i) => {
      const split = new SplitText(el, { type: "chars" });
      splitParagraphs.current.push(split);

      gsap.fromTo(
        split.chars,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.02,
          ease: "power2.out",
          delay: (headingRefs.current.length + i) * 0.1,
        }
      );
    });
  }, []);


  return (
    <div className='sa-auth-page'>
      {/* ── Left Hero Background ── */}
      <div className='sa-hero-bg'>
        <div className='sa-mesh-overlay'></div>
        <div className='sa-grid-lines'></div>
        <div className='sa-particle sa-particle-1'></div>
        <div className='sa-particle sa-particle-2'></div>
        <div className='sa-particle sa-particle-3'></div>
        <div className='sa-particle sa-particle-4'></div>
        <div className='sa-particle sa-particle-5'></div>
        <div className='sa-particle sa-particle-6'></div>
        <div className='sa-particle sa-particle-7'></div>
        <div className='sa-particle sa-particle-8'></div>
        <div className='sa-shape sa-shape-ring'></div>
        <div className='sa-shape sa-shape-hex'></div>
        <div className='sa-shape sa-shape-tri'></div>
      </div>

      <div className='sa-layout'>
        {/* ── Left Content ── */}
        <div className='sa-left-content'>
          <div className='sa-brand'>
            <div className='sa-brand-logo'>
              {currentBranding?.['isCustom'] && currentBranding?.['logo'] ? (
                <img src={currentBranding['logo']} alt={currentBranding['agencyName'] || 'Logo'} style={{ height: '42px', maxWidth: '160px', objectFit: 'contain' }} />
              ) : (
                <svg width="42" height="42" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="fpO1" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#e8709a" stopOpacity="0"/><stop offset="40%" stopColor="#ff9cc0"/><stop offset="60%" stopColor="#ff9cc0"/><stop offset="100%" stopColor="#e8709a" stopOpacity="0"/></linearGradient>
                    <linearGradient id="fpO2" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#c44070" stopOpacity="0"/><stop offset="40%" stopColor="#e8709a"/><stop offset="60%" stopColor="#e8709a"/><stop offset="100%" stopColor="#c44070" stopOpacity="0"/></linearGradient>
                    <radialGradient id="fpN" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#ffffff"/><stop offset="50%" stopColor="#ffb8d0"/><stop offset="100%" stopColor="#d4547a"/></radialGradient>
                    <filter id="fpG" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                  </defs>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(-40 27 27)" fill="none" stroke="url(#fpO1)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(40 27 27)" fill="none" stroke="url(#fpO2)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(90 27 27)" fill="none" stroke="url(#fpO1)" strokeWidth="1.2" opacity="0.7"/>
                  <circle cx="8.5" cy="27" r="3" fill="#ff9cc0" filter="url(#fpG)"/>
                  <circle cx="44.5" cy="22" r="2.4" fill="#e8709a" filter="url(#fpG)"/>
                  <circle cx="30" cy="8.5" r="2.4" fill="#c44070" filter="url(#fpG)"/>
                  <circle cx="27" cy="27" r="6.5" fill="rgba(84,21,48,0.5)" filter="url(#fpG)"/>
                  <circle cx="27" cy="27" r="5" fill="url(#fpN)"/>
                  <circle cx="27" cy="27" r="2.5" fill="white" opacity="0.95"/>
                </svg>
              )}
            </div>
            <span className='sa-brand-name'>
              {platformName}
            </span>
          </div>
          <div className='sa-hero-badge'>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Secure Password Recovery
          </div>
          <h1 className='sa-hero-headline'>Reset your<br />password in<br />seconds.</h1>
          <p className='sa-hero-subtitle'>Access predictive AI insights, automated follow-ups and always-on AI assistance.</p>

          <div className='sa-stats-grid'>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>3.2x</span>
              <span className='sa-stat-label'>Conversion</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>12k+</span>
              <span className='sa-stat-label'>Active Sellers</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>89%</span>
              <span className='sa-stat-label'>Time Saved</span>
            </div>
          </div>

          <div className='sa-testimonial'>
            <p className='sa-testimonial-text'>"{platformName} transformed our pipeline. We close 3x more deals with half the manual work. The AI follow-ups are a game-changer."</p>
            <div className='sa-testimonial-author'>
              <div className='sa-testimonial-avatar'>SK</div>
              <div>
                <strong>Sarah Kim</strong>
                <span>VP Sales, TechCorp</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Content ── */}
        <div className='sa-right-content'>
          <div className='sa-auth-card'>
            <div className='sa-card-icon'>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--sa-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>
            <h2 className='sa-card-title'>Reset Your Password</h2>
            <p className='sa-card-subtitle'>Enter your email address and we'll send you a code to reset your password.</p>

            <ValidatorForm className='sa-form' onSubmit={handleLogin}>
              <div className='sa-field'>
                <label className='sa-label'>Email</label>
                <TextValidator
                  type='text'
                  name='email'
                  value={email}
                  margin='dense'
                  variant='outlined'
                  placeholder='you@company.com'
                  className='sa-input'
                  validators={['required', 'isEmail']}
                  onChange={(e) => setEmail(e.target.value)}
                  errorMessages={['Email can not be empty', 'Email is not valid']}
                />
              </div>
              <button type='submit' className='sa-btn-primary' disabled={loading}>
                {!loading ? (
                  <>
                    Send Code
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </>
                ) : (
                  <i className='fa fa-spinner fa-spin'></i>
                )}
              </button>
            </ValidatorForm>

            <p className='sa-switch-text'>
              <Link to='/login'>Back to Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>

    /* ── OLD UI (commented out) ──
    <div className='login-page'>
      <div className='logo-area'>
        <></>
      </div>
      <div className='auto-container'>
        <div className='row'>
          <div className='col-lg-6 col-md-6 col-sm-12 login-area'>
            <div className='left-img-area forgot-password-img'>
              <img src={require('../../static/img/forgot-password.png')} alt='' />
              <h2>Sales AI</h2>
              <p>Access predictive AI insights, automated follow-ups and always-on AI assistance.</p>
            </div>
          </div>
          <div className='col-lg-6 col-md-6 col-sm-12 login-area'>
            <div className='login-form'>
              <div className='title-area'>
                <h2>Reset Your Password</h2>
                <p>Enter your email address and we'll send you a link to reset your password.</p>
              </div>
              <ValidatorForm className='validator-form mt-4' onSubmit={handleLogin}>
                ...
              </ValidatorForm>
            </div>
          </div>
        </div>
      </div>
    </div>
    */
  )
}

export default (withRouter(ForgotPassword))